import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  AlarmActivation,
  AlarmActivationSummary,
  ListHistoricalAlarmActivationsResponseBody,
} from '@repo/sdk/models/components';

import { useWebsocketQuery } from '@/hooks/query-hooks/websockets';
import { useGaiaClient } from '@/hooks/use-gaia-client';
import { activeAlarmActivationsQuery } from '@/queries/gaia-api/active-alarm-activations-query';
import { alarmActivationsSummaryQuery } from '@/queries/gaia-api/alarm-activations-summary-query';
import {
  listHistoricalAlarmActivationsInfiniteQuery,
  ListHistoricalAlarmActivationsInfiniteQueryProps,
} from '@/queries/gaia-api/list-historical-alarm-activations-infinite-query';
import { GaiaQueryWithAgentId } from '@/queries/types';

/**
 * Fetches the active alarm activations for this Agent from queryCache. This does
 * not make an HTTP request, instead it reads objects from queryCache that are populated
 * by listening to a subscription to the alarm activations.
 *
 * @param agentId The agent ID
 * @returns The active alarm activations for this Agent.
 */
export const useActiveAlarmActivations = ({
  agentId,
}: GaiaQueryWithAgentId): UseQueryResult<AlarmActivation[] | null> => {
  return useWebsocketQuery<AlarmActivation[] | null, AlarmActivation[] | null>(
    activeAlarmActivationsQuery({ agentId })
  );
};

/**
 * Fetches the alarm activation summary for this Agent from queryCache. This does
 * not make an HTTP request, instead it reads objects from queryCache that are populated
 * by listening to a subscription to the alarm activations.
 *
 * @param agentId The agent ID
 * @returns The alarm activation summary for this Agent.
 */
export const useAlarmActivationsSummary = ({
  agentId,
}: GaiaQueryWithAgentId): UseQueryResult<AlarmActivationSummary | null> => {
  return useWebsocketQuery<AlarmActivationSummary | null, AlarmActivationSummary | null>(
    alarmActivationsSummaryQuery({ agentId })
  );
};

/**
 * Fetches the historical alarm activations for this Agent from queryCache. This does
 * not make an HTTP request, instead it reads objects from queryCache that are populated
 * by listening to a subscription to the alarm activations.
 *
 * @param agentId The agent ID
 * @returns The historical alarm activations for this Agent.
 */
export const useListHistoricalAlarmActivationsInfinite = ({
  agentId,
}: Omit<ListHistoricalAlarmActivationsInfiniteQueryProps, 'client'>): UseInfiniteQueryResult<
  InfiniteData<ListHistoricalAlarmActivationsResponseBody, string> | undefined
> => {
  const client = useGaiaClient();
  return useInfiniteQuery(listHistoricalAlarmActivationsInfiniteQuery({ agentId, client }));
};
